import React, { useState, useEffect, useRef } from 'react';

const B = {
  bg:          '#faf7f2',
  surface:     '#ffffff',
  border:      'rgba(60,45,30,0.10)',
  borderStrong:'rgba(60,45,30,0.16)',
  text:        '#2a241d',
  muted:       '#6b6055',
  faint:       '#9a8f82',
  green:       '#7BC19E',
  greenDark:   '#5fa882',
  greenSoft:   '#eef6f1',
  charcoal:    '#3A3D3C',
};

const font = "'Nunito', -apple-system, sans-serif";

const GALLERY = [
  { src: '/marketing/desktop-coding-labs.jpg', label: 'Coding Labs', caption: 'Write real Python and Java, get instant feedback' },
  { src: '/marketing/desktop-flashcards.png',  label: 'Flashcards', caption: 'Spaced-repetition review across Theme A' },
  { src: '/marketing/desktop-sql.png',         label: 'SQL practice', caption: 'Write real queries, get instant feedback' },
  { src: '/marketing/desktop-quiz.png',        label: 'Quizzes', caption: 'Exam-style MCQs for every learning statement' },
  { src: '/marketing/desktop-cpu.png',         label: 'Simulators', caption: 'CPU, packet switching, recursion and more — step through it yourself' },
  { src: '/marketing/desktop-brookebits.jpg',  label: 'brookebits', caption: 'A free-play code scratchpad, separate from the syllabus' },
];

const FEATURES = [
  {
    title: 'Coding Labs',
    body: 'Write real Python and Java against the Theme B syllabus, with instant feedback and worked examples when you get stuck.',
    icon: <path d="M8 6 2 12l6 6M16 6l6 6-6 6" />,
  },
  {
    title: 'Theory Review',
    body: 'Spaced-repetition flashcards, exam-style multiple choice, and real SQL practice across the Theme A syllabus.',
    icon: <><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></>,
  },
  {
    title: 'Paper 2 Practice',
    body: 'Exam-style construct questions for SL and HL, Python and Java — original scenarios, graded automatically as you write them.',
    icon: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="m9 15 2 2 4-4" /></>,
  },
  {
    title: 'Interactive Simulators',
    body: 'CPU fetch-decode-execute, packet switching, recursion, logic gates, and AI case-study simulators you can drive step by step.',
    icon: <><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M9 9h6v6H9z" /></>,
  },
  {
    title: 'The Locker',
    body: 'XP, levels, badges, cosmetics and streaks that track real progress — not just a grade.',
    icon: <path d="M12 2 4 6v6c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6l-8-4z" />,
  },
  {
    title: 'brookebits',
    body: '"brookebits" is our name for free-play code snippets: a scratchpad outside the guided syllabus, for trying out an idea or practising for its own sake.',
    icon: <><path d="M4 17 10 11 4 5" /><path d="M12 19h8" /></>,
  },
];

const PRICING = [
  {
    name: 'Free',
    tag: 'Students & teachers',
    blurb: 'Enough to see if brookebit clicks for you.',
    bullets: [
      'Python coding across a sample of the Theme B syllabus',
      'One Theme A topic area — flashcards, quizzes and SQL',
      'Full Locker: XP, badges, streaks and cosmetics',
      'brookebits Python scratchpad',
      'Teachers: same free access as a student account',
    ],
    cta: 'free',
  },
  {
    name: 'Premium',
    tag: 'Individuals',
    blurb: 'Full syllabus coverage, for exam season and beyond.',
    bullets: [
      'Full Theme B syllabus, Python and Java',
      'Full Theme A syllabus, plus the Paper 2 Vault',
      'brookebits Java support, save and share your code',
    ],
    cta: 'coming_soon',
    highlight: true,
  },
  {
    name: 'Teacher',
    tag: 'For teachers',
    blurb: 'Everything in Premium, plus the tools to run a class.',
    bullets: [
      'Everything in Premium, on your own account',
      'Class rosters: invite and enrol your students',
      'Per-student and per-topic progress analytics',
      'Reporting for parents\' evenings and internal review',
    ],
    cta: 'coming_soon',
  },
  {
    name: 'School',
    tag: 'Departments & schools',
    blurb: 'For whole-department and whole-school rollouts.',
    bullets: [
      'One subscription covers every teacher and student',
      'Shared analytics across every class',
      'Set up and billed directly with us',
    ],
    cta: 'contact',
  },
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function Auth({ notice, onAuthSuccess, onNavigateToCPUS, onNavigateToPacks, onNavigateToHanoi, onNavigateToFractals, onNavigateToDiffusion, onNavigateToGan, onNavigateToConditional, onNavigateToLatent, onNavigateToFlow, onNavigateToCaverns }) {
  const [mode, setMode]           = useState('login'); // 'login' | 'signup' | 'signup-sent'
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [signupRole, setSignupRole] = useState('student');
  const [comingSoon, setComingSoon] = useState(false);
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [simsOpen, setSimsOpen] = useState(() => ['/cpus', '/packs', '/hanoi', '/fractals', '/diffusion', '/gan', '/conditional', '/latent', '/reversible', '/caverns'].includes((window.location.pathname || '').replace(/\/+$/, '')));
  const [lightboxIndex, setLightboxIndex] = useState(null); // index into GALLERY, or null when closed
  const emailRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const prefill = params.get('email');
    if (prefill) setEmail(prefill);
  }, []);

  useEffect(() => {
    if (drawerOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    document.body.style.overflow = 'hidden';
    const onKey = e => {
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowRight') setLightboxIndex(i => (i + 1) % GALLERY.length);
      if (e.key === 'ArrowLeft') setLightboxIndex(i => (i - 1 + GALLERY.length) % GALLERY.length);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [lightboxIndex]);

  // Signup is invite-only now: this page logs in existing accounts. New users
  // arrive via the emailed /accept-invite link, not a self-service form.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.reason || 'Authentication failed');
      localStorage.setItem('auth_token',    data.token || '');
      localStorage.setItem('student_id',    data.user_id || data.student_id);
      localStorage.setItem('account_type',  data.account_type || '');
      localStorage.setItem('display_name',  data.display_name || '');
      localStorage.setItem('active_avatar', data.active_avatar || 'avatar_default');
      localStorage.setItem('active_ring',   data.active_ring   || 'ring_none');
      localStorage.setItem('active_pet',    data.active_pet    || 'pet_none');
      onAuthSuccess(data.user_id || data.student_id, data.account_type, data.display_name || '');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Self-serve signup: posts to /api/signup-request, which always replies with
  // the same generic message (no account-existence enumeration) and emails an
  // invite link reusing the existing /accept-invite → /api/register flow.
  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    if (!EMAIL_RE.test(email)) { setError('Enter a valid email address.'); return; }
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/signup-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, role: signupRole }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.reason || 'Something went wrong. Please try again.');
      setComingSoon(!!data.coming_soon);
      setMode('signup-sent');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Used by CTAs below the fold (pricing cards) to bring the visitor back to
  // the signup card at the top rather than duplicating the form inline.
  const goToSignup = () => {
    setMode('signup');
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => emailRef.current?.focus(), 350);
  };

  return (
    <div style={{ fontFamily: font, background: B.bg, minHeight: '100vh', WebkitFontSmoothing: 'antialiased' }}>

      {/* ── NAV ─────────────────────────────────────────────────────── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, height: 62,
        background: B.surface, borderBottom: `1px solid ${B.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 1.5rem', zIndex: 100,
      }}>
        <a href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}>
          <img src="/logo.svg" alt="brookebit" height={46} style={{ display: 'block' }} />
        </a>
        <div className="auth-nav-actions" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button className="auth-nav-btn" onClick={() => { setMode('login'); setError(''); emailRef.current?.focus(); }} style={{
            background: 'none', color: B.text, border: `1.5px solid ${B.borderStrong}`, cursor: 'pointer',
            fontFamily: font, fontSize: '0.875rem', fontWeight: 700, whiteSpace: 'nowrap',
            padding: '0.4rem 0.9rem', borderRadius: 8, transition: 'border-color 0.15s',
          }}
            onMouseEnter={e => e.currentTarget.style.borderColor = B.green}
            onMouseLeave={e => e.currentTarget.style.borderColor = B.borderStrong}
          >Log in</button>
          <button className="auth-nav-btn" onClick={() => { setMode('signup'); setError(''); emailRef.current?.focus(); }} style={{
            background: B.green, color: '#fff', border: 'none', cursor: 'pointer',
            fontFamily: font, fontSize: '0.875rem', fontWeight: 700, whiteSpace: 'nowrap',
            padding: '0.45rem 1rem', borderRadius: 8, transition: 'background 0.15s',
          }}
            onMouseEnter={e => e.currentTarget.style.background = B.greenDark}
            onMouseLeave={e => e.currentTarget.style.background = B.green}
          >Sign up free</button>
          <button onClick={() => setDrawerOpen(true)} aria-label="Open menu" style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: 6, display: 'flex', flexDirection: 'column',
            justifyContent: 'center', gap: 4.5, borderRadius: 6,
          }}>
            {[0,1,2].map(i => (
              <span key={i} style={{ display: 'block', width: 20, height: 2, background: B.muted, borderRadius: 2 }} />
            ))}
          </button>
        </div>
      </nav>

      {/* ── HERO ────────────────────────────────────────────────────── */}
      <main style={{
        paddingTop: 62, minHeight: 'min(100vh, 780px)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '62px 1.5rem 2.5rem',
      }}>
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '3.5rem',
          maxWidth: 920, width: '100%', padding: '3rem 0',
          flexWrap: 'wrap',
        }}>

          {/* Left column: copy + video */}
          <div style={{ flex: 1, minWidth: 280 }}>
            <h1 style={{
              fontSize: 'clamp(1.75rem, 4vw, 2.6rem)', fontWeight: 800,
              lineHeight: 1.15, color: B.text, letterSpacing: '-0.5px',
              marginBottom: '1rem', margin: '0 0 1rem',
            }}>
              IB Computer Science,<br />
              <em style={{ fontStyle: 'normal', color: B.green }}>made interactive.</em>
            </h1>
            <p style={{ color: B.muted, fontSize: '1rem', lineHeight: 1.65, marginBottom: '0.6rem', margin: '0 0 0.6rem' }}>
              Stop reading about Computer Science and start doing it. Write and run real Python and Java, drill the full theory, and practise Paper 2 until it's second nature, all in your browser. No setup, no installs.
            </p>
            <p style={{ color: B.faint, fontSize: '0.82rem', lineHeight: 1.65, marginTop: '1rem', margin: '1rem 0 1.5rem' }}>
              Every Theme A and Theme B topic, HL and SL, with a growing suite of interactive simulators that bring the hardest ideas to life, from logic gates to machine learning.
            </p>

            {/* Video demo */}
            <video
              autoPlay loop muted playsInline
              style={{
                width: '100%', borderRadius: 16,
                boxShadow: '0 8px 24px rgba(50,30,15,0.10)',
                border: `1px solid ${B.border}`,
                display: 'block',
              }}
            >
              <source src="/brookebit3.mp4" type="video/mp4" />
            </video>
          </div>

          {/* Card */}
          <div style={{
            background: B.surface, borderRadius: 16,
            boxShadow: '0 1px 0 rgba(255,255,255,0.6) inset, 0 8px 24px rgba(50,30,15,0.07)',
            border: `1px solid ${B.border}`, padding: '2rem 1.75rem 1.75rem',
            width: 300, flexShrink: 0,
          }}>
            {mode === 'signup-sent' ? (
              <>
                <h2 style={{ fontSize: '1.2rem', fontWeight: 800, color: B.charcoal, margin: '0 0 1rem' }}>
                  {comingSoon ? 'Coming soon' : 'Check your email'}
                </h2>
                <p style={{ color: B.muted, fontSize: '0.86rem', lineHeight: 1.6, margin: '0 0 1.25rem' }}>
                  {comingSoon
                    ? "Self-serve signup isn't open yet — check back soon! Already enrolled by your school? Check your email for an invite link, or log in below."
                    : <>If <strong>{email}</strong> can sign up, we've sent a link to set your
                      password and get started. The link is valid for 14 days.</>}
                </p>
                <button onClick={() => { setMode('login'); setError(''); }} style={{
                  width: '100%', padding: '0.72rem', background: 'none',
                  color: B.text, border: `1.5px solid ${B.borderStrong}`, borderRadius: 8,
                  fontSize: '0.9rem', fontWeight: 800, cursor: 'pointer',
                  letterSpacing: '0.5px', fontFamily: font,
                }}>← BACK TO LOG IN</button>
              </>
            ) : (
              <>
                <h2 style={{ fontSize: '1.2rem', fontWeight: 800, color: B.charcoal, margin: '0 0 1.5rem' }}>
                  {mode === 'signup'
                    ? <>Create your free brooke<span style={{ color: B.green }}>bit</span> account</>
                    : <>Log in to brooke<span style={{ color: B.green }}>bit</span></>}
                </h2>

                {notice && !error && (
                  <div style={{
                    padding: '10px 14px', background: '#eef3fb', color: '#2f4f7a',
                    border: '1px solid #d6e2f3', borderRadius: 8,
                    fontSize: '0.85rem', marginBottom: '1rem',
                  }}>{notice}</div>
                )}

                {error && (
                  <div style={{
                    padding: '10px 14px', background: '#fdf2f2', color: '#c0392b',
                    border: '1px solid #fbe2e2', borderRadius: 8,
                    fontSize: '0.85rem', marginBottom: '1rem',
                  }}>{error}</div>
                )}

                {mode === 'signup' ? (
                  <form onSubmit={handleSignup}>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: B.text, marginBottom: '0.35rem' }}>Email</label>
                      <input
                        ref={emailRef}
                        type="email" required value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="you@school.com"
                        autoComplete="email"
                        style={inputStyle}
                        onFocus={e => { e.target.style.borderColor = B.green; e.target.style.boxShadow = '0 0 0 3px rgba(123,193,158,0.18)'; }}
                        onBlur={e  => { e.target.style.borderColor = B.borderStrong; e.target.style.boxShadow = 'none'; }}
                      />
                    </div>

                    <div style={{ marginBottom: 0 }}>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: B.text, marginBottom: '0.5rem' }}>I am a...</label>
                      <div style={{ display: 'flex', gap: '0.6rem' }}>
                        {[['student', 'Student'], ['teacher', 'Teacher']].map(([value, label]) => (
                          <button key={value} type="button" onClick={() => setSignupRole(value)} style={{
                            flex: 1, padding: '0.55rem', borderRadius: 8, cursor: 'pointer',
                            fontFamily: font, fontSize: '0.85rem', fontWeight: 700,
                            border: `1.5px solid ${signupRole === value ? B.green : B.borderStrong}`,
                            background: signupRole === value ? B.greenSoft : 'none',
                            color: signupRole === value ? B.greenDark : B.muted,
                            transition: 'border-color 0.15s, background 0.15s',
                          }}>{label}</button>
                        ))}
                      </div>
                    </div>

                    <button type="submit" disabled={loading} style={{
                      width: '100%', padding: '0.72rem', background: B.charcoal,
                      color: '#fff', border: 'none', borderRadius: 8,
                      fontSize: '0.9rem', fontWeight: 800, cursor: loading ? 'not-allowed' : 'pointer',
                      marginTop: '1.25rem', letterSpacing: '0.5px', fontFamily: font,
                      opacity: loading ? 0.7 : 1, transition: 'background 0.15s',
                    }}
                      onMouseEnter={e => { if (!loading) e.currentTarget.style.background = '#2a2c2b'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = B.charcoal; }}
                    >
                      {loading ? 'Sending…' : 'CREATE FREE ACCOUNT'}
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: B.text, marginBottom: '0.35rem' }}>Email</label>
                      <input
                        ref={emailRef}
                        type="email" required value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="you@school.com"
                        autoComplete="email"
                        style={inputStyle}
                        onFocus={e => { e.target.style.borderColor = B.green; e.target.style.boxShadow = '0 0 0 3px rgba(123,193,158,0.18)'; }}
                        onBlur={e  => { e.target.style.borderColor = B.borderStrong; e.target.style.boxShadow = 'none'; }}
                      />
                    </div>

                    <div style={{ marginBottom: 0 }}>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: B.text, marginBottom: '0.35rem' }}>Password</label>
                      <input
                        type="password" required value={password}
                        onChange={e => setPassword(e.target.value)}
                        placeholder="••••••••"
                        autoComplete="current-password"
                        style={inputStyle}
                        onFocus={e => { e.target.style.borderColor = B.green; e.target.style.boxShadow = '0 0 0 3px rgba(123,193,158,0.18)'; }}
                        onBlur={e  => { e.target.style.borderColor = B.borderStrong; e.target.style.boxShadow = 'none'; }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.35rem' }}>
                        <a href="/forgot-password"
                          style={{ color: B.greenDark, fontSize: '0.78rem', fontWeight: 600, fontFamily: font, textDecoration: 'none' }}>
                          Forgot password?
                        </a>
                      </div>
                    </div>

                    <button type="submit" disabled={loading} style={{
                      width: '100%', padding: '0.72rem', background: B.charcoal,
                      color: '#fff', border: 'none', borderRadius: 8,
                      fontSize: '0.9rem', fontWeight: 800, cursor: loading ? 'not-allowed' : 'pointer',
                      marginTop: '1.25rem', letterSpacing: '0.5px', fontFamily: font,
                      opacity: loading ? 0.7 : 1, transition: 'background 0.15s',
                    }}
                      onMouseEnter={e => { if (!loading) e.currentTarget.style.background = '#2a2c2b'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = B.charcoal; }}
                    >
                      {loading ? 'Logging in…' : 'LOG IN'}
                    </button>
                  </form>
                )}

                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  margin: '1.25rem 0', color: B.faint, fontSize: '0.75rem',
                }}>
                  <span style={{ flex: 1, height: 1, background: B.border }} />
                  {mode === 'signup' ? 'already have an account?' : 'new here?'}
                  <span style={{ flex: 1, height: 1, background: B.border }} />
                </div>

                <div style={{ textAlign: 'center', fontSize: '0.83rem', color: B.muted, lineHeight: 1.55 }}>
                  {mode === 'signup' ? (
                    <button onClick={() => { setMode('login'); setError(''); }} style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                      color: B.greenDark, fontWeight: 700, fontSize: '0.83rem', fontFamily: font,
                    }}>Log in instead</button>
                  ) : (
                    <>
                      Free to get started — no invite needed.{' '}
                      <button onClick={() => { setMode('signup'); setError(''); }} style={{
                        background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                        color: B.greenDark, fontWeight: 700, fontSize: '0.83rem', fontFamily: font,
                      }}>Create a free account</button>.
                      {' '}Already enrolled by your school? Check your email for an invite link.
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <button
          onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
          aria-label="Scroll down to see what's included"
          style={{
            background: 'none', border: 'none', cursor: 'pointer', marginTop: '2.5rem',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem',
            color: B.faint, fontFamily: font, fontSize: '0.75rem', fontWeight: 700,
            letterSpacing: '0.4px', textTransform: 'uppercase',
          }}
        >
          See what's included
          <svg className="auth-scroll-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
      </main>

      {/* ── FEATURE HIGHLIGHTS ──────────────────────────────────────── */}
      <section id="features" style={{ padding: '0 1.5rem 4rem', maxWidth: 1040, margin: '0 auto', scrollMarginTop: 62 }}>
        <h2 style={{
          textAlign: 'center', color: B.text, fontSize: 'clamp(1.4rem, 3vw, 1.8rem)',
          fontWeight: 800, margin: '0 0 2.25rem',
        }}>Everything you need for IB Computer Science</h2>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '1.25rem',
        }}>
          {FEATURES.map(f => (
            <div key={f.title} style={{
              background: B.surface, borderRadius: 14, border: `1px solid ${B.border}`,
              boxShadow: '0 6px 18px rgba(50,30,15,0.05)', padding: '1.5rem 1.4rem',
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10, background: B.greenSoft,
                display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem',
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={B.greenDark}
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  {f.icon}
                </svg>
              </div>
              <div style={{ fontWeight: 800, fontSize: '1rem', color: B.text, marginBottom: '0.4rem' }}>{f.title}</div>
              <div style={{ fontSize: '0.86rem', color: B.muted, lineHeight: 1.55 }}>{f.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── PRICING ─────────────────────────────────────────────────── */}
      <section style={{ padding: '0 1.5rem 4rem', maxWidth: 1040, margin: '0 auto' }}>
        <h2 style={{
          textAlign: 'center', color: B.text, fontSize: 'clamp(1.4rem, 3vw, 1.8rem)',
          fontWeight: 800, margin: '0 0 0.6rem',
        }}>Start free. Upgrade when you're ready.</h2>
        <p style={{ textAlign: 'center', color: B.muted, fontSize: '0.9rem', margin: '0 0 2.25rem' }}>
          Every account — student or teacher — starts on the free tier. No card required.
        </p>
        <div className="auth-pricing-grid" style={{
          display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem', alignItems: 'stretch',
        }}>
          {PRICING.map(tier => (
            <div key={tier.name} style={{
              background: B.surface, borderRadius: 16, padding: '1.75rem 1.5rem',
              border: tier.highlight ? `2px solid ${B.green}` : `1px solid ${B.border}`,
              boxShadow: tier.highlight ? '0 10px 28px rgba(123,193,158,0.18)' : '0 6px 18px rgba(50,30,15,0.05)',
              display: 'flex', flexDirection: 'column',
            }}>
              {tier.tag && (
                <div style={{
                  display: 'inline-block', alignSelf: 'flex-start', background: B.greenSoft, color: B.greenDark,
                  fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.4px', textTransform: 'uppercase',
                  padding: '0.2rem 0.55rem', borderRadius: 999, marginBottom: '0.6rem',
                }}>{tier.tag}</div>
              )}
              <div style={{ fontWeight: 800, fontSize: '1.15rem', color: B.charcoal, marginBottom: '0.3rem' }}>{tier.name}</div>
              <div style={{ color: B.muted, fontSize: '0.85rem', marginBottom: '1.25rem' }}>{tier.blurb}</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.5rem', flex: 1 }}>
                {tier.bullets.map(b => (
                  <li key={b} style={{
                    display: 'flex', gap: '0.55rem', fontSize: '0.85rem', color: B.text,
                    lineHeight: 1.5, marginBottom: '0.6rem',
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={B.greenDark}
                      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                      style={{ flexShrink: 0, marginTop: 2 }}>
                      <path d="m5 13 4 4L19 7" />
                    </svg>
                    {b}
                  </li>
                ))}
              </ul>
              {tier.cta === 'free' && (
                <button onClick={goToSignup} style={{
                  width: '100%', padding: '0.7rem', background: B.charcoal, color: '#fff',
                  border: 'none', borderRadius: 8, fontSize: '0.86rem', fontWeight: 800,
                  cursor: 'pointer', letterSpacing: '0.3px', fontFamily: font,
                }}>GET STARTED FREE</button>
              )}
              {tier.cta === 'coming_soon' && (
                <button onClick={goToSignup} style={{
                  width: '100%', padding: '0.7rem', background: B.greenSoft, color: B.greenDark,
                  border: `1.5px solid ${B.green}`, borderRadius: 8, fontSize: '0.86rem', fontWeight: 800,
                  cursor: 'pointer', letterSpacing: '0.3px', fontFamily: font,
                }}>COMING SOON</button>
              )}
              {tier.cta === 'contact' && (
                <a href="mailto:info@brookebit.com?subject=School%20licence%20enquiry" style={{
                  display: 'block', width: '100%', padding: '0.7rem', background: 'none', color: B.text,
                  border: `1.5px solid ${B.borderStrong}`, borderRadius: 8, fontSize: '0.86rem', fontWeight: 800,
                  cursor: 'pointer', letterSpacing: '0.3px', fontFamily: font, textAlign: 'center',
                  textDecoration: 'none', boxSizing: 'border-box',
                }}>CONTACT US</a>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── SCREENSHOT GALLERY ──────────────────────────────────────── */}
      <section style={{ padding: '0 0 4rem', overflow: 'hidden' }}>
        <p style={{
          textAlign: 'center', color: B.faint, fontSize: '0.78rem',
          fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase',
          margin: '0 0 1.25rem',
        }}>See it in action</p>
        <div className="auth-gallery-track">
          {[...GALLERY, ...GALLERY].map((item, i) => (
            <button key={i} onClick={() => setLightboxIndex(i % GALLERY.length)}
              aria-label={`Expand screenshot: ${item.label}`}
              className="auth-gallery-card" style={{
              background: B.surface, borderRadius: 14,
              border: `1px solid ${B.border}`,
              boxShadow: '0 6px 18px rgba(50,30,15,0.07)',
              overflow: 'hidden', flexShrink: 0, width: 320,
              padding: 0, cursor: 'pointer', textAlign: 'left', fontFamily: font,
              transition: 'transform 0.15s, box-shadow 0.15s',
            }}>
              <div style={{ width: '100%', height: 190, overflow: 'hidden', background: B.bg }}>
                <img src={item.src} alt={item.label} style={{
                  width: '100%', height: '100%',
                  objectFit: 'cover', objectPosition: 'top center', display: 'block',
                }} />
              </div>
              <div style={{ padding: '0.85rem 1rem' }}>
                <div style={{ fontWeight: 800, fontSize: '0.88rem', color: B.text }}>{item.label}</div>
                <div style={{ fontSize: '0.78rem', color: B.muted, marginTop: 2 }}>{item.caption}</div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* ── SCREENSHOT LIGHTBOX ─────────────────────────────────────── */}
      {lightboxIndex !== null && (
        <div
          onClick={() => setLightboxIndex(null)}
          role="dialog" aria-modal="true" aria-label={GALLERY[lightboxIndex].label}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(42,36,29,0.75)',
            zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '2rem 1.25rem', animation: 'fadeIn 0.15s ease',
          }}
        >
          <button onClick={() => setLightboxIndex(null)} aria-label="Close" style={{
            position: 'fixed', top: '1.25rem', right: '1.25rem', zIndex: 301,
            background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff',
            width: 40, height: 40, borderRadius: '50%', cursor: 'pointer',
            fontSize: '1.4rem', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>×</button>

          <button onClick={e => { e.stopPropagation(); setLightboxIndex(i => (i - 1 + GALLERY.length) % GALLERY.length); }}
            aria-label="Previous screenshot" style={{
            position: 'fixed', left: '1rem', top: '50%', transform: 'translateY(-50%)', zIndex: 301,
            background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff',
            width: 44, height: 44, borderRadius: '50%', cursor: 'pointer',
            fontSize: '1.3rem', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>‹</button>
          <button onClick={e => { e.stopPropagation(); setLightboxIndex(i => (i + 1) % GALLERY.length); }}
            aria-label="Next screenshot" style={{
            position: 'fixed', right: '1rem', top: '50%', transform: 'translateY(-50%)', zIndex: 301,
            background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff',
            width: 44, height: 44, borderRadius: '50%', cursor: 'pointer',
            fontSize: '1.3rem', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>›</button>

          <div onClick={e => e.stopPropagation()} style={{
            maxWidth: 'min(1100px, 92vw)', maxHeight: '88vh',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
          }}>
            <img src={GALLERY[lightboxIndex].src} alt={GALLERY[lightboxIndex].label} style={{
              maxWidth: '100%', maxHeight: '72vh', borderRadius: 12,
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)', display: 'block', objectFit: 'contain',
            }} />
            <div style={{ textAlign: 'center', marginTop: '1rem', color: '#fff' }}>
              <div style={{ fontWeight: 800, fontSize: '1rem' }}>{GALLERY[lightboxIndex].label}</div>
              <div style={{ fontSize: '0.85rem', opacity: 0.75, marginTop: 2 }}>{GALLERY[lightboxIndex].caption}</div>
            </div>
          </div>
        </div>
      )}

      {/* ── DRAWER ──────────────────────────────────────────────────── */}
      {drawerOpen && (
        <div onClick={() => setDrawerOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(42,36,29,0.35)',
          zIndex: 200, animation: 'fadeIn 0.2s ease',
        }} />
      )}
      <aside style={{
        position: 'fixed', top: 0, right: 0, width: 260, height: '100vh',
        background: B.surface, zIndex: 201, padding: '1.5rem',
        borderLeft: `1px solid ${B.border}`,
        transform: drawerOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.25s ease',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem' }}>
          <img src="/logo.svg" alt="brookebit" height={28} style={{ display: 'block' }} />
          <button onClick={() => setDrawerOpen(false)} aria-label="Close menu" style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: B.muted, fontSize: '1.4rem', lineHeight: 1, padding: '2px 6px', borderRadius: 4,
          }}>×</button>
        </div>
        <nav style={{ display: 'flex', flexDirection: 'column' }}>
          <button onClick={() => setSimsOpen(o => !o)} aria-expanded={simsOpen} style={{
            display: 'flex', alignItems: 'center', padding: '0.85rem 0', width: '100%', textAlign: 'left',
            borderBottom: `1px solid ${B.border}`, borderTop: 'none', borderLeft: 'none', borderRight: 'none',
            background: 'none', cursor: 'pointer', color: B.text, fontSize: '0.95rem', fontWeight: 700, fontFamily: font,
          }}>
            Simulators
            <span style={{ marginLeft: 'auto', display: 'inline-flex', transform: simsOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s ease' }}>
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="6 3 11 8 6 13" />
              </svg>
            </span>
          </button>
          {simsOpen && (
          <div style={{ marginLeft: '0.6rem', paddingLeft: '0.65rem', borderLeft: `2px solid ${B.border}` }}>
          {onNavigateToCPUS && (
            <button onClick={() => { setDrawerOpen(false); onNavigateToCPUS(); }} style={{
              display: 'block', padding: '0.85rem 0', width: '100%', textAlign: 'left',
              borderBottom: `1px solid ${B.border}`, borderTop: 'none', borderLeft: 'none', borderRight: 'none',
              background: 'none', cursor: 'pointer', color: B.text,
              fontSize: '0.95rem', fontWeight: 600, fontFamily: font,
            }}
              onMouseEnter={e => e.currentTarget.style.color = B.greenDark}
              onMouseLeave={e => e.currentTarget.style.color = B.text}
            >CPU Simulator</button>
          )}
          {onNavigateToPacks && (
            <button onClick={() => { setDrawerOpen(false); onNavigateToPacks(); }} style={{
              display: 'block', padding: '0.85rem 0', width: '100%', textAlign: 'left',
              borderBottom: `1px solid ${B.border}`, borderTop: 'none', borderLeft: 'none', borderRight: 'none',
              background: 'none', cursor: 'pointer', color: B.text,
              fontSize: '0.95rem', fontWeight: 600, fontFamily: font,
            }}
              onMouseEnter={e => e.currentTarget.style.color = B.greenDark}
              onMouseLeave={e => e.currentTarget.style.color = B.text}
            >Packet Switching</button>
          )}
          {onNavigateToHanoi && (
            <button onClick={() => { setDrawerOpen(false); onNavigateToHanoi(); }} style={{
              display: 'block', padding: '0.85rem 0', width: '100%', textAlign: 'left',
              borderBottom: `1px solid ${B.border}`, borderTop: 'none', borderLeft: 'none', borderRight: 'none',
              background: 'none', cursor: 'pointer', color: B.text,
              fontSize: '0.95rem', fontWeight: 600, fontFamily: font,
            }}
              onMouseEnter={e => e.currentTarget.style.color = B.greenDark}
              onMouseLeave={e => e.currentTarget.style.color = B.text}
            >Towers of Hanoi</button>
          )}
          {onNavigateToFractals && (
            <button onClick={() => { setDrawerOpen(false); onNavigateToFractals(); }} style={{
              display: 'block', padding: '0.85rem 0', width: '100%', textAlign: 'left',
              borderBottom: `1px solid ${B.border}`, borderTop: 'none', borderLeft: 'none', borderRight: 'none',
              background: 'none', cursor: 'pointer', color: B.text,
              fontSize: '0.95rem', fontWeight: 600, fontFamily: font,
            }}
              onMouseEnter={e => e.currentTarget.style.color = B.greenDark}
              onMouseLeave={e => e.currentTarget.style.color = B.text}
            >Fractal Drawing</button>
          )}
          {onNavigateToDiffusion && (
            <button onClick={() => { setDrawerOpen(false); onNavigateToDiffusion(); }} style={{
              display: 'block', padding: '0.85rem 0', width: '100%', textAlign: 'left',
              borderBottom: `1px solid ${B.border}`, borderTop: 'none', borderLeft: 'none', borderRight: 'none',
              background: 'none', cursor: 'pointer', color: B.text,
              fontSize: '0.95rem', fontWeight: 600, fontFamily: font,
            }}
              onMouseEnter={e => e.currentTarget.style.color = B.greenDark}
              onMouseLeave={e => e.currentTarget.style.color = B.text}
            >Diffusion Denoising</button>
          )}
          {onNavigateToGan && (
            <button onClick={() => { setDrawerOpen(false); onNavigateToGan(); }} style={{
              display: 'block', padding: '0.85rem 0', width: '100%', textAlign: 'left',
              borderBottom: `1px solid ${B.border}`, borderTop: 'none', borderLeft: 'none', borderRight: 'none',
              background: 'none', cursor: 'pointer', color: B.text,
              fontSize: '0.95rem', fontWeight: 600, fontFamily: font,
            }}
              onMouseEnter={e => e.currentTarget.style.color = B.greenDark}
              onMouseLeave={e => e.currentTarget.style.color = B.text}
            >GAN Loop</button>
          )}
          {onNavigateToConditional && (
            <button onClick={() => { setDrawerOpen(false); onNavigateToConditional(); }} style={{
              display: 'block', padding: '0.85rem 0', width: '100%', textAlign: 'left',
              borderBottom: `1px solid ${B.border}`, borderTop: 'none', borderLeft: 'none', borderRight: 'none',
              background: 'none', cursor: 'pointer', color: B.text,
              fontSize: '0.95rem', fontWeight: 600, fontFamily: font,
            }}
              onMouseEnter={e => e.currentTarget.style.color = B.greenDark}
              onMouseLeave={e => e.currentTarget.style.color = B.text}
            >Conditional Generation</button>
          )}
          {onNavigateToLatent && (
            <button onClick={() => { setDrawerOpen(false); onNavigateToLatent(); }} style={{
              display: 'block', padding: '0.85rem 0', width: '100%', textAlign: 'left',
              borderBottom: `1px solid ${B.border}`, borderTop: 'none', borderLeft: 'none', borderRight: 'none',
              background: 'none', cursor: 'pointer', color: B.text,
              fontSize: '0.95rem', fontWeight: 600, fontFamily: font,
            }}
              onMouseEnter={e => e.currentTarget.style.color = B.greenDark}
              onMouseLeave={e => e.currentTarget.style.color = B.text}
            >VAE Latent Space</button>
          )}
          {onNavigateToFlow && (
            <button onClick={() => { setDrawerOpen(false); onNavigateToFlow(); }} style={{
              display: 'block', padding: '0.85rem 0', width: '100%', textAlign: 'left',
              borderBottom: `1px solid ${B.border}`, borderTop: 'none', borderLeft: 'none', borderRight: 'none',
              background: 'none', cursor: 'pointer', color: B.text,
              fontSize: '0.95rem', fontWeight: 600, fontFamily: font,
            }}
              onMouseEnter={e => e.currentTarget.style.color = B.greenDark}
              onMouseLeave={e => e.currentTarget.style.color = B.text}
            >Flow Reversibility</button>
          )}
          {onNavigateToCaverns && (
            <button onClick={() => { setDrawerOpen(false); onNavigateToCaverns(); }} style={{
              display: 'block', padding: '0.85rem 0', width: '100%', textAlign: 'left',
              borderBottom: `1px solid ${B.border}`, borderTop: 'none', borderLeft: 'none', borderRight: 'none',
              background: 'none', cursor: 'pointer', color: B.text,
              fontSize: '0.95rem', fontWeight: 600, fontFamily: font,
            }}
              onMouseEnter={e => e.currentTarget.style.color = B.greenDark}
              onMouseLeave={e => e.currentTarget.style.color = B.text}
            >Boolean Caverns</button>
          )}
          </div>
          )}
          {[
            { label: 'About', href: '/about' },
            { label: 'Contact', href: '/contact' },
            { label: 'Privacy Policy', href: '/privacy' },
            { label: 'Terms of Use', href: '/terms' },
          ].map(({ label, href }) => (
            <a key={label} href={href} style={{
              display: 'block', padding: '0.85rem 0',
              borderBottom: `1px solid ${B.border}`,
              textDecoration: 'none', color: B.text,
              fontSize: '0.95rem', fontWeight: 600, fontFamily: font,
            }}
              onMouseEnter={e => e.currentTarget.style.color = B.greenDark}
              onMouseLeave={e => e.currentTarget.style.color = B.text}
            >{label}</a>
          ))}
        </nav>
      </aside>

      <style>{`
        @keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes scrollBounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(5px); } }
        .auth-scroll-chevron { animation: scrollBounce 1.6s ease-in-out infinite; }
        @keyframes authGalleryScroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .auth-gallery-track {
          display: flex; gap: 1.25rem; width: max-content;
          animation: authGalleryScroll 32s linear infinite;
        }
        .auth-gallery-track:hover { animation-play-state: paused; }
        .auth-gallery-card:hover { transform: translateY(-3px); box-shadow: 0 10px 26px rgba(50,30,15,0.14) !important; }
        @media (max-width: 400px) {
          .auth-nav-btn { font-size: 0.78rem !important; padding: 0.4rem 0.6rem !important; }
          .auth-nav-actions { gap: 0.4rem !important; }
        }
        @media (min-width: 640px) {
          .auth-pricing-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '0.6rem 0.85rem',
  border: '1.5px solid rgba(60,45,30,0.16)',
  borderRadius: 8, fontSize: '0.95rem',
  color: '#2a241d', background: '#faf7f2',
  outline: 'none', fontFamily: "'Nunito', sans-serif",
  transition: 'border-color 0.15s, box-shadow 0.15s',
  boxSizing: 'border-box',
};
